<script lang="ts">
	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	// Services and configs
	import { devices_config } from 'src/configs/devices';
	// Helpers
	import { get_mode, real_id, lightDevicesWithoutModeSwitch } from 'src/helpers/interface_helpers';
	import { isXMODDevice, isRgbwwPm } from 'src/helpers/interface_helpers';
	import {
		isNightModeEnabled,
		isDeviceOff,
		supportsColorModeChange,
	} from 'src/helpers/night_mode_helpers';
	import { isRgbCct } from 'src/helpers/interface_helpers';
	// Design System Components
	import { SidepanelContent, ControlsContainer } from '@Shelly/shelly-design-system';
	// Components
	import ControlMode from 'src/components/Controls/Single/ControlMode.svelte';
	import ControlColor from 'src/components/Controls/Single/ControlColor.svelte';
	import ControlTemp from 'src/components/Controls/Single/ControlTemp.svelte';
	import ControlEffects from 'src/components/Controls/Single/ControlEffects.svelte';
	// Types
	import type { Device } from 'src/types/Device';
	import { is_gen_2 } from 'src/helpers/interface_helpers';

	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: mode = get_mode(device, status, settings);
	$: deviceConfig = devices_config?.[device?.type];
	const isGen2 = is_gen_2(device);

	// Check if device has color control and color temp control (matching DeviceDrawer logic)
	$: hasColorTempControl = isXMODDevice(device?.type)
		? status?.['cct:0'] && device?.mode === 'cct'
		: deviceConfig?.modes?.[mode]?.color_temp;
	$: hasColorControl = isXMODDevice(device?.type)
		? status?.['rgbw:0'] && device?.mode === 'rgbw'
		: deviceConfig?.modes?.[mode]?.color;

	// Effects check (matching DeviceDrawer logic)
	$: hasEffects = !isGen2 ? status?.lights : false;

	// XMOD and RGBWW PM specific checks
	$: isXMODCct = isXMODDevice(device?.type) && device?.mode === 'cct';
	$: isXMODRgbw = isXMODDevice(device?.type) && device?.mode === 'rgbw';
	$: isRgbwwPmCct = isRgbwwPm(device?.type) && device?.mode === 'cct';

	// Secondary controls disabled state (matching DeviceDrawer logic)
	$: secondaryControlsDisabled =
		status?.online && settings
			? isDeviceOff(device, status) &&
				isNightModeEnabled(device, settings) &&
				(supportsColorModeChange(device.type) || isRgbCct(device.type))
			: false;
</script>

<SidepanelContent>
	{#if device && typeof status !== 'undefined' && typeof settings !== 'undefined' && typeof deviceConfig !== 'undefined'}
		<div class="secondary-controls-wrapper" class:disabled={secondaryControlsDisabled}>
			{#if !isXMODDevice(device?.type) && (hasColorControl || hasColorTempControl) && !lightDevicesWithoutModeSwitch(device.type)}
				<ControlMode {device} {settings} {mode} bind:disabled={secondaryControlsDisabled} />
			{:else if hasColorControl && hasEffects}
				<ControlsContainer color="color-0" class="mb-m">
					<div slot="first">
						<ControlEffects {device} />
					</div>
				</ControlsContainer>
			{/if}

			{#if isXMODCct || isRgbwwPmCct || mode === 'white' || mode === 'cct' || (mode === 'rgbcct' && settings?.[`${mode}:0`]?.mode === 'cct')}
				<ControlTemp {device} {status} />
			{:else if isXMODRgbw || !isXMODDevice(device?.type)}
				<ControlColor {device} {status} />
			{/if}
		</div>
	{:else}
		<div class="loading-state">
			<div class="flex-full-height"></div>
		</div>
	{/if}
</SidepanelContent>
